/* TODO LIST
- Loading state
*/

const PROMO_TILE_SIZES =
  '(min-width: 750px) calc((100vw - 1rem) / 4), calc((100vw - 1rem) / 2)';

// Enforced by the Composition API, which rejects the whole request with a 400
// once the request asks for more than this many disjunctive facets.
const MAX_DISJUNCTIVE_FACETS = 20;

// Algolia returns this many values per facet by default, so it is the number of
// options the filter rail renders per group. The composition asks for the most
// Algolia allows instead, so that sorting the values afterwards chooses from
// the full set rather than from Algolia's highest-count slice, then trims back
// to this.
const MAX_VALUES_PER_FACET = 100;
const MAX_VALUES_PER_FACET_REQUESTED = 1000;

// Regional replica suffixes from buildIndexParams(), mapped to the dataset key
// part of each region's optional composition ID (data-composition-id-<region>
// and data-composition-id-fr-<region>).
const REGIONAL_COMPOSITION_KEYS = {
  _QC_Atlantic: 'QcAtlantic',
  _Prairies: 'Prairies',
  _BC: 'Bc',
};

/**
 * Whether a hit should get the Staff Pick badge set in JD Settings.
 *
 * By default the badge only marks tagged products a Smart Group placed
 * (smartGroupKey, set by normalizeCompositionResult). The "show outside Smart
 * Groups" setting extends it to every tagged product on every index. The tag
 * and label are merchant-entered settings, so a blank value turns the badge
 * off rather than matching nothing or rendering an empty badge. Shopify treats
 * tags as case-insensitive, so the match does too.
 */
function shouldShowStaffPick(hit) {
  const cards = window.cards || {};
  const tag = String(cards.staff_pick_tag || '').trim().toLowerCase();
  const label = String(cards.staff_pick_label || '').trim();

  if (!cards.staff_pick_enabled || !tag || !label || !Array.isArray(hit.tags)) {
    return false;
  }

  if (!cards.staff_pick_show_everywhere && !hit.smartGroupKey) {
    return false;
  }

  return hit.tags.some(
    (hitTag) => String(hitTag).trim().toLowerCase() === tag
  );
}

const prefersReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
).matches;

function buildPromoTileSrcset(imageUrl) {
  if (!imageUrl) {
    return '';
  }
  return `${imageUrl}&width=180 180w, ${imageUrl}&width=360 360w, ${imageUrl}&width=540 540w, ${imageUrl}&width=720 720w, ${imageUrl}&width=900 900w, ${imageUrl}&width=1080 1080w`;
}

function createPromoTileImage({ src, width, height, className }) {
  const tileImage = document.createElement('img');
  tileImage.classList.add('motion-reduce');
  if (className) {
    tileImage.classList.add(className);
  }
  tileImage.loading = 'lazy';
  tileImage.width = width;
  tileImage.height = height;
  tileImage.src = src;
  tileImage.srcset = buildPromoTileSrcset(src);
  tileImage.sizes = PROMO_TILE_SIZES;
  tileImage.alt = '';
  return tileImage;
}

function createPromoTileVideo({ src, poster, width, height, className }) {
  const tileVideo = document.createElement('video');
  tileVideo.classList.add('promo-loop-video');
  if (className) {
    tileVideo.classList.add(className);
  }
  tileVideo.src = src;
  if (poster) {
    tileVideo.poster = poster;
  }
  tileVideo.width = width;
  tileVideo.height = height;
  tileVideo.muted = true;
  tileVideo.loop = true;
  tileVideo.playsInline = true;
  tileVideo.setAttribute('muted', '');
  tileVideo.setAttribute('playsinline', '');
  tileVideo.setAttribute('webkit-playsinline', '');
  tileVideo.autoplay = false;
  tileVideo.controls = false;
  tileVideo.preload = 'none';
  tileVideo.setAttribute('aria-hidden', 'true');
  return tileVideo;
}

function observePromoLoopVideos(root) {
  if (!('IntersectionObserver' in window) || prefersReducedMotion) {
    return;
  }

  if (!root._promoVideoObserver) {
    root._promoVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: '100px 0px', threshold: 0.25 }
    );
  }

  root
    .querySelectorAll('video.promo-loop-video:not([data-observed])')
    .forEach((video) => {
      video.dataset.observed = 'true';
      root._promoVideoObserver.observe(video);
    });
}

/**
 * Use algoliaShopify for all product relateed data, including:
 * - Filters
 * - Sorting
 * - Pagination
 */
class ListingAlgolia extends HTMLElement {
  constructor() {
    super();

    // Component data
    this.loaded = false;
    this.indexAdjust = 0;
    this.collectionHandle = this.dataset.collectionHandle;
    this.collectionId = this.dataset.collectionId;
    this.sectionId = this.dataset.sectionId;
    this.searchTerms = this.dataset.searchTerms || '';
    this.queryId = '';

    this.init();  
  }

  init() {
     const collectionPromoScript = document.getElementById('ColectionPromos');
    if (collectionPromoScript) {
      try {
        this.collectionPromos = JSON.parse(collectionPromoScript.textContent);
      } catch (error) {
        console.error('Failed to parse ColectionPromos JSON', error);
        this.collectionPromos = null;
      }
    }    

    // Algolia search client
    this.searchClient = window['algoliasearch/lite'].liteClient(
      algoliaShopify.config.app_id,
      algoliaShopify.config.search_api_key,
      {
        baseHeaders: {
          'X-Algolia-UserToken': getAlgoliaUserToken(),
        },
      }
    );

    this.initCompositionClient();

    // Initial state
    const collectionQueryRuleValue = algoliaShopify.config
      .collection_id_query_rules
      ? this.collectionId
      : this.collectionHandle;

    this.analyticsTags = [
      collectionQueryRuleValue,
      'collection',
      getDeviceType(),
    ];

    this.hitsPerPage =
      algoliaShopify.config.products_full_results_hits_per_page;
    this.currentPage = 0;

    this.currentSortBy = '';
    this.currentFilters = {};

    this.sortData =
      algoliaShopify.config.collection_sort_orders &&
      algoliaShopify.config.collection_sort_orders[this.collectionId]
        ? algoliaShopify.config.collection_sort_orders[this.collectionId]
        : algoliaShopify.config.sort_orders;

    this.initFacetCatalog();

    // Facet names from Algolia's facetOrdering, keyed by index + rule context.
    this.facetOrderCache = {};

    this.setStatesFromURL();

    // DOM elements
    this.pillsContainer = document.querySelector('#AlgoliaFilterPills');
    this.itemCounts = document.querySelectorAll('.AlgoliaItemCount');
    this.sortBy = document.querySelector('#AlgoliaSortBy');
    this.sortByDrawer = document.querySelector('#AlgoliaDrawerSortBy');
    this.filtersDesktop = document.querySelector('#AlgoliaDesktopFilters');
    this.filtersDrawer = document.querySelector('#AlgoliaDrawerFilters');
    this.productList = document.querySelector('#AlgoliaProductGrid');
    this.paginationContainer = document.querySelector('#AlgoliaPagination');

    // Events
    this.debouncedOnSubmit = debounce((event) => {
      this.onFilterChange(event);
    }, 800);

    if (this.filtersDesktop) {
      this.filtersDesktop.addEventListener(
        'input',
        this.debouncedOnSubmit.bind(this)
      );
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.addEventListener(
        'input',
        this.debouncedOnSubmit.bind(this)
      );
    }

    if (this.sortBy) {
      this.generateSortFromConfig();
      this.sortBy.addEventListener('change', this.onSortChange.bind(this));
      this.sortByDrawer.addEventListener(
        'change',
        this.onSortChange.bind(this)
      );
    }

    if (this.paginationContainer) {
      this.paginationContainer
        .querySelector('.pagination__select')
        .addEventListener('change', this.onPaginationSelect.bind(this));

      this.paginationContainer
        .querySelector('.pagination__button--prev')
        .addEventListener('click', this.onPaginationPrev.bind(this));

      this.paginationContainer
        .querySelector('.pagination__button--next')
        .addEventListener('click', this.onPaginationNext.bind(this));
    }

    window.addEventListener('popstate', (event) => {
      this.currentFilters = event.state ? event.state.searchParams : {};
      this.currentSortBy = event.state ? event.state.sortBy : '';
      this.currentPage = event.state ? event.state.page : 0;

      this.setDOMStates();
      this.search();
    });

    // Initial search. We require consent tracking to be loaded first.
    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      this.search.bind(this, true)
    );
  }

  disconnectedCallback() {
    if (this.onAlgoliaLoadedUnsubscriber) {
      this.onAlgoliaLoadedUnsubscriber();
    }

    if (this.onSwatchChangeUnsubscriber) {
      this.onSwatchChangeUnsubscriber();
    }
  }

  connectedCallback() {
    // If its a collection, send an Algolia Viewed Event
    this.onAlgoliaLoadedUnsubscriber = subscribe(
      PUB_SUB_EVENTS.algoliaLoaded,
      () => {
        if (this.collectionHandle) {
          window.Shopify.loadFeatures(
            [
              {
                name: 'consent-tracking-api',
                version: '0.1',
              },
            ],
            () => {
              aa('viewedFilters', {
                userToken: getAlgoliaUserToken(),
                index: this.buildIndexParams(),
                eventName: 'Landing Page Viewed',
                filters: [`collections:${this.collectionHandle}`],
              });
            }
          );
        }
      }
    );

    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchChange,
      (target) => {
        aa('viewedObjectIDs', {
          userToken: getAlgoliaUserToken(),
          eventName: 'Product Viewed',
          index: this.buildIndexParams(),
          objectIDs: [String(target.dataset.variantId)],
        });
      }
    );
  }

  /**
   * Set the current parameters from the URL.
   * Only needs to do filters and sorting since those are the only
   * parameters that are not handled by the algoliaShopify API.
   */
  setStatesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    urlParams.forEach((value, key) => {
      if (key == 'page') {
        this.currentPage = parseInt(value);
      } else if (key == 'sort_by') {
        this.currentSortBy = value;
      } else if (this.isFacetParam(key)) {
        if (!this.currentFilters[key]) {
          this.currentFilters[key] = [];
        }

        this.currentFilters[key].push(value);
      }
    });
  }

  /**
   * Set the DOM states from the current parameters.
   */
  setDOMStates() {
    this.sortBy.value = this.currentSortBy;
    this.sortByDrawer.querySelectorAll('input').forEach((input) => {
      if (input.value == this.currentSortBy) {
        input.checked = true;
      } else {
        input.checked = false;
      }
    });

    Object.keys(this.currentFilters).forEach((filter) => {
      this.currentFilters[filter].forEach((value) => {
        // Values come from the URL, so escape them before building a selector.
        const selector = `input[name="${CSS.escape(filter)}"][value="${CSS.escape(value)}"]`;

        if (this.filtersDesktop) {
          const filterElement = this.filtersDesktop.querySelector(selector);

          if (filterElement) {
            filterElement.checked = true;
          }
        }

        if (this.filtersDrawer) {
          const filterElement = this.filtersDrawer.querySelector(selector);

          if (filterElement) {
            filterElement.checked = true;
          }
        }
      });
    });
  }

  /**
   * Reset the product list.
   */
  resetProductList() {
    this.indexAdjust = 0;
    this.currentPage = 0;
    this.productList.innerHTML = '';
  }

  /**
   * Search algoliaShopify for the product data.
   * @param {boolean} fullBuild - Whether to fully rebuild the product list and filters. It'll only be false when a filter or sort is applied.
   */
  search(fullBuild = true, paginationTriggered = false) {
    this.cleanEmptyFilters();
    if (fullBuild && this.loaded) {
      this.resetProductList();
    }

    /**
     * This is used to build the search params for the algoliaShopify API.
     *
     * We need to send multiple requests in one search because in algolia, facet data is conjunctive.
     * Meaning, naturally, you can't apply multiple filters to the same facet.
     *
     * So, we need to build a query for each facet that is applied.
     *
     * For example, if you have a filter for color and a filter for size, you need to build two queries in addition to the default query.
     *
     * The first query will be the default query, which includes the color and size facetFilters.
     * The second query will be the query will only include the color facetFilters.
     * The third query will be the query will only include the size facetFilters.
     *
     * The first query will then be used to build the product list and the pagination, since this is the "true" query
     * that the customer is wanting to see.
     *
     * For all facets that are not applied (lets say: 'type'), the first query will ALSO be used. Since we want to
     * only show the filters that can actually be applied with the current filters.
     *
     * The second and third queries will be used to build their respective filters. The way it works is that
     * the facetFilters that are applied to the query include all filters except those in the same group.
     *
     * So for query 2, the facetFilters will be the color facetFilters, but not the size facetFilters.
     * Meaning that it will return all size filters, that can be applied with the current color filters.
     *
     * The same goes for query 3.
     *
     * IF there are no filters applied, the first query will be used to build ALL the filters.
     */

    const facetCacheKey = this.getFacetCacheKey();

    this.runListingSearch().then((results) => {
      if (paginationTriggered) {
        this.productList.innerHTML = '';
      }

      this.rememberFacetOrdering(facetCacheKey, results[0]);

      this.queryId = results[0].queryID;

      this.generateProductsFromHits(results[0].hits, results[0].queryID);
      this.generatePaginationFromResults(results[0]);
      this.generateCountsFromResults(results[0]);

      this.generatePillsFromFacets();

      if (fullBuild) {
        const facetLocation = {};

        Object.keys(this.currentFilters).forEach((facet, index) => {
          facetLocation[facet] = index + 1;
        });

        this.generateFiltersFromSearchResults(results, facetLocation);
      }

      if (paginationTriggered) {
        // Scroll to the top of the product list
        this.productList.scrollIntoView({ behavior: 'smooth' });
      }
    });

    this.loaded = true;
  }

  /**
   * Build the index params for the algoliaShopify API. The 'index' is the algolia product list to search.
   * @returns {string} The index params.
   */
  buildIndexParams() {
    const prairies = ['AB', 'MB', 'SK'];
    const atlantic = ['QC', 'PE', 'NS', 'NB', 'NL'];
    let currentRegion;

    try {
      currentRegion = Shopify.customerPrivacy.getRegion();
    } catch (error) {
      currentRegion = 'CAON';
    }

    const countryCode = currentRegion.slice(0, 2);
    const regionCode = currentRegion.slice(-2);

    if (this.currentSortBy != '') {
      return `${algoliaShopify.config.index_prefix}products${
        window.theme.locale == 'fr_CA' ? '_canada_fr' : ''
      }${this.currentSortBy}`;
    } else if (prairies.includes(regionCode)) {
      return `${algoliaShopify.config.index_prefix}products${
        window.theme.locale == 'fr_CA' ? '_canada_fr' : ''
      }_Prairies`;
    } else if (atlantic.includes(regionCode)) {
      return `${algoliaShopify.config.index_prefix}products${
        window.theme.locale == 'fr_CA' ? '_canada_fr' : ''
      }_QC_Atlantic`;
    } else if (regionCode == 'BC') {
      return `${algoliaShopify.config.index_prefix}products${
        window.theme.locale == 'fr_CA' ? '_canada_fr' : ''
      }_BC`;
    } else {
      return `${algoliaShopify.config.index_prefix}products${
        window.theme.locale == 'fr_CA' ? '_canada_fr' : ''
      }`;
    }
  }

  buildSearchQuery() {
    if (this.searchTerms) {
      return this.searchTerms;
    }

    return '';
  }

  /**
   * Build the search params for the algoliaShopify API.
   * @returns {object} The search params.
   */
  buildSearchParams() {
    const facetParams = this.buildFacetParams();

    const params = {
      page: this.currentPage,
      hitsPerPage: this.hitsPerPage,

      // Context, controls wether rules should be applied
      ruleContexts: this.buildRuleContexts(),

      // The Algolia dashboard (facetOrdering) decides which facets show, so
      // request the facets it lists. No request, no values.
      facets: this.getRequestedFacets(this.getFacetCacheKey()),
      maxValuesPerFacet: 100,
      sortFacetValuesBy: 'count',

      // Filters to be used for the search
      filters: this.buildFilterParams(),
      facetFilters: facetParams, // By default, we want to just apply the filters that are applied to the DOM.

      // List of product attributes to be returned
      attributesToRetrieve: ['*'],

      // Hard coded params
      distinct: true,
      clickAnalytics: true,
      enablePersonalization: false,
      userToken: getAlgoliaUserToken(),
      analyticsTags: this.analyticsTags,
    };

    return params;
  }

  /**
   * Build the multiple search params for the algoliaShopify API.
   * @returns {object} The multiple search params.
   */
  buildMultipleSearchParams() {
    const queries = [
      {
        indexName: this.buildIndexParams(),
        query: this.buildSearchQuery(),
        ...this.buildSearchParams(),
      },
    ];

    // Check if any filters are applied. If not, we can just use the default query.
    // Otherwise, we need to build a query for each facet that is applied.
    Object.keys(this.currentFilters).forEach((facet) => {
      const facetData = [];

      Object.keys(this.currentFilters).forEach((appliedFacet) => {
        if (facet !== appliedFacet) {
          const facetGroup = [];

          this.currentFilters[appliedFacet].forEach((value) => {
            facetGroup.push(`${appliedFacet}:${value}`);
          });

          facetData.push(facetGroup);
        }
      });

      queries.push({
        indexName: this.buildIndexParams(),
        query: '',
        ...this.buildSearchParams(),
        hitsPerPage: 1,
        facetFilters: facetData,
      });
    });

    return queries;
  }

  /**
   * Set up the Composition API client used to return Smart Groups.
   *
   * While the theme setting is off, nothing here runs and the listing keeps
   * using the Search API exactly as before.
   */
  initCompositionClient() {
    const readId = (key) => String(this.dataset[key] || '').trim();

    // One main composition per language, attached to that language's primary
    // products index. Regional and sorted views are replicas of the primary,
    // which the main composition can reach through its sortingStrategy.
    this.compositionIds = {
      en: readId('compositionId'),
      fr: readId('compositionIdFr'),
    };

    // Optional composition per region, built on that region's replica. When
    // set, it serves that region instead of the main composition.
    this.regionalCompositionIds = { en: {}, fr: {} };

    Object.keys(REGIONAL_COMPOSITION_KEYS).forEach((suffix) => {
      const key = REGIONAL_COMPOSITION_KEYS[suffix];

      this.regionalCompositionIds.en[suffix] = readId(`compositionId${key}`);
      this.regionalCompositionIds.fr[suffix] = readId(`compositionIdFr${key}`);
    });

    const anyCompositionId = [
      this.compositionIds.en,
      this.compositionIds.fr,
      ...Object.values(this.regionalCompositionIds.en),
      ...Object.values(this.regionalCompositionIds.fr),
    ].some((id) => id !== '');

    this.compositionEnabled =
      this.dataset.compositionEnabled === 'true' &&
      anyCompositionId &&
      typeof AlgoliaCompositionClient !== 'undefined';

    this.compositionClient = this.compositionEnabled
      ? new AlgoliaCompositionClient(
          algoliaShopify.config.app_id,
          algoliaShopify.config.search_api_key,
          { userToken: getAlgoliaUserToken() }
        )
      : null;

    // compositionId -> Promise<sortingStrategy>, so concurrent searches share
    // one lookup.
    this.sortingStrategyRequests = new Map();
  }

  /**
   * Decide whether the current view can be served by a composition.
   *
   * Resolves to { compositionId, sortBy } or null for the Search API, checking
   * in this order:
   * 1. The region's own composition, if its ID is set. It is built on the
   *    regional replica, so it needs no sortBy.
   * 2. The language's main composition. The primary index needs no sortBy. A
   *    regional or sorted replica is only used when the composition's
   *    sortingStrategy maps it: the API ignores an unknown sortBy and answers
   *    from the primary index, which would show the shopper the wrong region
   *    or sort order, so an unmapped replica is rejected here.
   * 3. Otherwise null, so the Search API answers without Smart Groups.
   * Never rejects; any lookup problem resolves to null.
   */
  resolveCompositionTarget() {
    if (!this.compositionEnabled) {
      return Promise.resolve(null);
    }

    const isFrench = window.theme.locale == 'fr_CA';
    const language = isFrench ? 'fr' : 'en';
    const compositionId = this.compositionIds[language];
    const indexName = this.buildIndexParams();
    const primaryIndex = `${algoliaShopify.config.index_prefix}products${
      isFrench ? '_canada_fr' : ''
    }`;

    const indexSuffix = indexName.startsWith(primaryIndex)
      ? indexName.slice(primaryIndex.length)
      : '';
    const regionalCompositionId = Object.prototype.hasOwnProperty.call(
      REGIONAL_COMPOSITION_KEYS,
      indexSuffix
    )
      ? this.regionalCompositionIds[language][indexSuffix]
      : '';

    if (regionalCompositionId) {
      return Promise.resolve({
        compositionId: regionalCompositionId,
        sortBy: '',
      });
    }

    if (!compositionId) {
      console.info(
        `[Algolia Composition] no ${
          isFrench ? 'French' : 'English'
        } composition ID set, using the Search API:`,
        indexName
      );
      return Promise.resolve(null);
    }

    if (indexName === primaryIndex) {
      return Promise.resolve({ compositionId, sortBy: '' });
    }

    return this.getSortingStrategy(compositionId).then((strategy) => {
      // The client may key an entry by the replica name or by any label, so
      // accept either as long as it points at this replica.
      const sortBy = Object.keys(strategy).find(
        (label) => label === indexName || strategy[label] === indexName
      );

      if (!sortBy) {
        console.info(
          '[Algolia Composition] replica not in the sorting strategy, using the Search API:',
          { compositionId, indexName }
        );
        return null;
      }

      return { compositionId, sortBy };
    });
  }

  /**
   * Fetch a composition's sortingStrategy ({ label: replicaIndex }), cached in
   * sessionStorage for the browser session so the lookup costs one request,
   * not one per search. A failed lookup resolves to {} (Search API) and is not
   * cached in storage, so the next page load tries again.
   */
  getSortingStrategy(compositionId) {
    if (this.sortingStrategyRequests.has(compositionId)) {
      return this.sortingStrategyRequests.get(compositionId);
    }

    const storageKey = `algoliaCompositionStrategy:${compositionId}`;
    const maxAge = 15 * 60 * 1000;

    try {
      const cached = JSON.parse(sessionStorage.getItem(storageKey) || 'null');

      if (cached && Date.now() - cached.savedAt < maxAge) {
        const request = Promise.resolve(cached.strategy);
        this.sortingStrategyRequests.set(compositionId, request);
        return request;
      }
    } catch (error) {
      // Storage blocked or unreadable; fall through to the network.
    }

    const request = this.compositionClient
      .getComposition(compositionId)
      .then((composition) => {
        const strategy = this.sanitizeSortingStrategy(
          composition && composition.sortingStrategy
        );

        try {
          sessionStorage.setItem(
            storageKey,
            JSON.stringify({ savedAt: Date.now(), strategy })
          );
        } catch (error) {
          // Storage blocked or full; the in-memory promise still serves this page.
        }

        return strategy;
      })
      .catch((error) => {
        console.warn(
          '[Algolia Composition] could not read the sorting strategy, using the Search API for replicas:',
          error.message
        );
        return {};
      });

    this.sortingStrategyRequests.set(compositionId, request);
    return request;
  }

  /**
   * Keep only string-to-string entries from a sortingStrategy, so a malformed
   * value from the API can't reach the request or the lookup.
   */
  sanitizeSortingStrategy(strategy) {
    const clean = {};

    if (!strategy || typeof strategy !== 'object' || Array.isArray(strategy)) {
      return clean;
    }

    Object.keys(strategy).forEach((label) => {
      const indexName = strategy[label];

      if (label.trim() && typeof indexName === 'string' && indexName.trim()) {
        clean[label] = indexName.trim();
      }
    });

    return clean;
  }

  /**
   * Run the query behind the listing.
   *
   * Results come back in the Search API's shape either way, so everything that
   * renders the grid, pagination, counts and filters stays the same regardless
   * of which API answered.
   */
  runListingSearch() {
    return this.resolveCompositionTarget()
      .catch(() => null)
      .then((target) => {
        if (!target) {
          return this.runSearchApiSearch();
        }

        return this.runCompositionSearch(target).catch((error) => {
          // A composition failure must never leave the shopper looking at an
          // empty grid, so fall back to the Search API for this request.
          console.warn(
            '[Algolia Composition] request failed, falling back to the Search API:',
            error.message,
            error.status ? `(status ${error.status})` : ''
          );

          return this.runSearchApiSearch();
        });
      });
  }

  runSearchApiSearch() {
    return this.searchClient
      .search({ requests: this.buildMultipleSearchParams() })
      .then((queries) => queries.results);
  }

  runCompositionSearch({ compositionId, sortBy }) {
    const { params, dropped } = this.buildCompositionParams(sortBy);

    return this.compositionClient
      .run(compositionId, params)
      .then((response) => {
        const result = response.results ? response.results[0] : response;
        const normalized = this.normalizeCompositionResult(
          result,
          compositionId
        );

        window.__algoliaCompositionLastResponse = response;

        const compositionInfo =
          (result.compositions && result.compositions[compositionId]) || {};

        console.info(
          `[Algolia Composition] ${normalized.nbHits} hits, ${
            normalized.hits.filter((hit) => hit.smartGroupKey).length
          } placed by a Smart Group`,
          {
            compositionId,
            sortBy: sortBy || '(primary index)',
            index: result.index,
            groups: (compositionInfo.injectedItems || []).map(
              (item) => item.key
            ),
            disjunctiveFacets: params.facets.filter((facet) =>
              facet.startsWith('disjunctive(')
            ),
            droppedParams: dropped,
            fullResponse: 'window.__algoliaCompositionLastResponse',
          }
        );

        // Returning a single result keeps filter rendering on its single-result
        // path, which builds every filter from this response's facets. The
        // disjunctive facets above make those counts match what the Search
        // API's extra per-facet queries used to return.
        return [normalized];
      });
  }

  /**
   * Reshape a composition result into the Search API's result shape.
   *
   * The two are close enough that this is mostly pass-through. The useful part
   * is flagging which hits a Smart Group placed, so that rendering a badge on
   * them later does not mean digging back through _rankingInfo.
   */
  normalizeCompositionResult(result, compositionId) {
    const hits = (result.hits || []).map((hit) => {
      const composed = hit._rankingInfo && hit._rankingInfo.composed;
      const placement = composed && composed[compositionId];

      if (!placement) {
        return hit;
      }

      return { ...hit, smartGroupKey: placement.injectedItemKey };
    });

    return {
      ...result,
      hits,
      facets: this.sortFacetValues(result.facets),
    };
  }

  /**
   * Order each facet's values the way sortFacetValuesBy: 'alpha' does.
   *
   * The filter rail renders facet values in the order the response lists them,
   * and the Search API is asked to sort them alphabetically. The composition
   * rejects that param and returns values by descending count instead, so the
   * sort happens here to keep the rail in the order shoppers expect.
   *
   * Algolia compares the values character by character while ignoring case,
   * which is why 'adidas' sits with the other A's. localeCompare is not used
   * because it reorders punctuation, which would put '10:25' ahead of
   * '100:150' in the price filter and move apostrophes and underscores around
   * in brand and tag values.
   */
  sortFacetValues(facets) {
    const sorted = {};

    Object.keys(facets || {}).forEach((facet) => {
      sorted[facet] = {};

      Object.keys(facets[facet])
        .sort((left, right) => {
          const a = left.toLowerCase();
          const b = right.toLowerCase();

          if (a === b) {
            return 0;
          }

          return a < b ? -1 : 1;
        })
        .slice(0, MAX_VALUES_PER_FACET)
        .forEach((value) => {
          sorted[facet][value] = facets[facet][value];
        });
    });

    return sorted;
  }

  /**
   * Translate the current Search API params into composition params.
   *
   * The composition endpoint rejects any param outside its own list, so this
   * filters against that list rather than spreading the search params through.
   * Today that drops distinct, attributesToRetrieve and sortFacetValuesBy,
   * which the Search API accepts but Composition does not. sortBy is only
   * passed for a replica that resolveCompositionTarget() found in the
   * composition's sortingStrategy.
   */
  buildCompositionParams(sortBy = '') {
    const allowedParams = [
      'analytics',
      'analyticsTags',
      'clickAnalytics',
      'enablePersonalization',
      'enableRules',
      'facetFilters',
      'facets',
      'filters',
      'getRankingInfo',
      'hitsPerPage',
      'numericFilters',
      'optionalFilters',
      'page',
      'query',
      'relevancyStrictness',
      'ruleContexts',
      'sortBy',
      'userToken',
    ];

    const searchParams = {
      ...this.buildSearchParams(),
      query: this.buildSearchQuery(),
    };

    const params = {};
    const dropped = [];

    Object.keys(searchParams).forEach((param) => {
      if (allowedParams.includes(param)) {
        params[param] = searchParams[param];
      } else {
        dropped.push(param);
      }
    });

    return {
      params: {
        ...params,

        // Exposes _rankingInfo.composed on each hit, which is how a product
        // placed by a Smart Group is identified. Also required for the badge,
        // so this must stay switched on.
        getRankingInfo: true,

        facets: this.buildDisjunctiveFacets(),
        maxValuesPerFacet: MAX_VALUES_PER_FACET_REQUESTED,

        ...(sortBy ? { sortBy } : {}),
      },
      dropped,
    };
  }

  /**
   * Mark the facets that currently have a filter applied as disjunctive.
   *
   * A filtered facet otherwise comes back holding only the values that were
   * selected, so the shopper loses the rest of that group and cannot pick a
   * second value in it. The modifier counts a facet as though its own group
   * were unfiltered, while every other group's filters still apply, which is
   * what the extra query per applied facet in buildMultipleSearchParams does
   * for the Search API.
   *
   * Only applied facets are wrapped, since an unfiltered facet already returns
   * the right values. That also keeps the request well inside the API's
   * disjunctive facet limit, and the slice holds it there even if a shopper
   * somehow opens filters in more groups than the limit allows.
   *
   * The facet list is the one the Search API path requests
   * (getRequestedFacets), so the Algolia dashboard decides which facets show
   * whichever API answers. While that list is still ['*'], applied facets are
   * not named in it, so they are added as disjunctive entries alongside it.
   */
  buildDisjunctiveFacets() {
    const applied = Object.keys(this.currentFilters)
      .filter(
        (facet) =>
          Array.isArray(this.currentFilters[facet]) &&
          this.currentFilters[facet].length > 0
      )
      .slice(0, MAX_DISJUNCTIVE_FACETS);

    const requested = this.getRequestedFacets(this.getFacetCacheKey());

    const facets = requested.map((facet) =>
      applied.includes(facet) ? `disjunctive(${facet})` : facet
    );

    applied.forEach((facet) => {
      if (!requested.includes(facet)) {
        facets.push(`disjunctive(${facet})`);
      }
    });

    return facets;
  }

  buildFilterParams() {
    // This is used to apply any filter that is not from a facet.
    // Basically, anything that does not come from a filter on the DOM.
    // This will probably not ever have anything other than the collection handle.
    const params = [];

    if (this.collectionHandle) {
      params.push(`collections:${this.collectionHandle}`);
    }

    return params.join(' AND ');
  }

  buildFacetParams() {
    // This is used to apply any filter that is from a facet.
    // A facet filter is a filter from the DOM.
    const params = [];

    Object.keys(this.currentFilters).forEach((facet) => {
      const facetGroup = [];

      this.currentFilters[facet].forEach((value) => {
        facetGroup.push(`${facet}:${value}`);
      });

      params.push(facetGroup);
    });

    return params;
  }

  /**
   * Generate the sort options from the config.
   */
  generateSortFromConfig() {
    if (this.sortBy || this.sortByDrawer) {
      this.sortData.forEach((sort) => {
        var sortOption = false;

        if (sort.asc && sort.asc.active) {
          sortOption = sort.asc;
        }

        if (sort.desc && sort.desc.active) {
          sortOption = sort.desc;
        }

        if (sortOption) {
          const option = document.createElement('option');
          option.value = `_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          option.innerHTML = sortOption.title;

          const li = document.createElement('li');
          li.classList.add('filters__item', 'list-menu__item');

          const label = document.createElement('label');
          label.classList.add('filters__label', 'facets__label');
          label.htmlFor = `Filter_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;

          const input = document.createElement('input');
          input.classList.add('mobile-facets__checkbox');
          input.type = 'radio';
          input.name = 'sort_by';
          input.value = `_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          input.id = `Filter_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          input.checked = this.currentSortBy == option.value;

          const span = document.createElement('span');
          span.classList.add('facet-checkbox__text-label');
          span.innerHTML = sortOption.title;

          label.appendChild(input);
          label.appendChild(span);
          li.appendChild(label);

          if (this.currentSortBy == option.value) {
            option.selected = true;
          }

          if (this.sortBy) {
            this.sortBy.appendChild(option.cloneNode(true));

            if (this.currentSortBy == option.value) {
              this.sortBy.value = option.value;
            }
          }

          if (this.sortByDrawer) {
            this.sortByDrawer.appendChild(li.cloneNode(true));
          }
        }
      });
    }
  }

  /**
   * Build the facet catalog from the Algolia Shopify app config.
   * The app supplies facet titles (for any facet, enabled or not) and the
   * enabled list used only when the Algolia dashboard sends no facetOrdering.
   */
  initFacetCatalog() {
    const byName = {};
    const orderedNames = [];

    const addFacets = (facets) => {
      if (!Array.isArray(facets)) {
        return;
      }

      facets.forEach((facet) => {
        if (!facet || !facet.name) {
          return;
        }

        if (!byName[facet.name]) {
          byName[facet.name] = facet;
          orderedNames.push(facet.name);
        } else if (facet.enabled) {
          byName[facet.name] = facet;
        }
      });
    };

    const collectionFacets =
      algoliaShopify.config.collection_facets &&
      algoliaShopify.config.collection_facets[this.collectionId];

    addFacets(collectionFacets);
    addFacets(algoliaShopify.config.facets);

    this.facetDataByName = byName;
    this.facetData = orderedNames.map((name) => byName[name]);
    this.facetFilters = orderedNames.filter(
      (name) => byName[name] && byName[name].enabled
    );
  }

  getFacetConfig(facetName) {
    if (this.facetDataByName && this.facetDataByName[facetName]) {
      return this.facetDataByName[facetName];
    }

    // Facet listed in the Algolia dashboard but not in the app: derive a label.
    const label = (facetName.split('.').pop() || facetName)
      .replace(/[_-]/g, ' ')
      .trim();

    return {
      name: facetName,
      title: label.charAt(0).toUpperCase() + label.slice(1),
      enabled: true,
    };
  }

  getFacetOrdering(result) {
    return (
      result &&
      result.renderingContent &&
      result.renderingContent.facetOrdering
    );
  }

  /**
   * Whether a URL param is a facet filter. Accepts any facet from the app
   * config, plus attribute-style names the Algolia dashboard can surface.
   * The character whitelist keeps marketing params (utm_*, etc.) out.
   * @param {string} key - URL param name.
   * @returns {boolean}
   */
  isFacetParam(key) {
    if (typeof key !== 'string' || !key) {
      return false;
    }

    if (this.facetDataByName && this.facetDataByName[key]) {
      return true;
    }

    return /^(named_tags|options|meta)\.[\w .#-]+$/.test(key);
  }

  /**
   * Rule contexts for the main query. Collection rules apply only while no
   * facet filter is selected.
   * @returns {string[]}
   */
  buildRuleContexts() {
    const context = [];

    if (this.dataset.collectionHandle && this.buildFacetParams().length == 0) {
      if (algoliaShopify.config.collection_id_query_rules) {
        context.push(`${this.dataset.collectionId}`);
      } else {
        context.push(`${this.dataset.collectionHandle}`);
      }
    }

    return context;
  }

  /**
   * facetOrdering can differ per index (locale, region, sort) and per rule
   * context, so the cached facet list is keyed by both.
   * @returns {string}
   */
  getFacetCacheKey() {
    return `${this.buildIndexParams()}|${this.buildRuleContexts().join(',')}`;
  }

  /**
   * Facets to request. Until Algolia's facet order for this index/context is
   * known, request all ('*') so every dashboard facet gets values. After that,
   * request only the dashboard facets (plus applied ones) to keep payloads small.
   * @param {string} cacheKey - From getFacetCacheKey().
   * @returns {string[]}
   */
  getRequestedFacets(cacheKey) {
    const known = this.facetOrderCache && this.facetOrderCache[cacheKey];

    if (!known) {
      return ['*'];
    }

    const names = new Set(known);

    for (const name of Object.keys(this.currentFilters)) {
      names.add(name);
    }

    return Array.from(names);
  }

  /**
   * Cache the facets Algolia's dashboard lists for this index/context.
   * Without a dashboard order, cache the app's enabled list (the fallback).
   * @param {string} cacheKey - From getFacetCacheKey().
   * @param {object} result - The main Algolia result.
   */
  rememberFacetOrdering(cacheKey, result) {
    if (!this.facetOrderCache || !result) {
      return;
    }

    const facetOrdering = this.getFacetOrdering(result);
    const order =
      facetOrdering && facetOrdering.facets && facetOrdering.facets.order;
    const names = Array.isArray(order)
      ? order.filter((name) => typeof name === 'string' && name)
      : [];

    this.facetOrderCache[cacheKey] = names.length
      ? names
      : this.facetFilters.slice();
  }

  /**
   * Facets to render, in display order.
   * Prefer Algolia's Facet Display / Dynamic Facets order when present.
   * @param {object} facetOrdering - renderingContent.facetOrdering
   * @returns {string[]}
   */
  getOrderedFacetNames(facetOrdering) {
    const hiddenFacets = new Set(
      (facetOrdering && facetOrdering.facets && facetOrdering.facets.hide) || []
    );
    const orderedFacets =
      facetOrdering && facetOrdering.facets && facetOrdering.facets.order;

    if (Array.isArray(orderedFacets) && orderedFacets.length) {
      return orderedFacets.filter((name) => !hiddenFacets.has(name));
    }

    return this.facetFilters.filter((name) => !hiddenFacets.has(name));
  }

  /**
   * Apply pinned, hidden, and remaining-value sort rules from Facet Display.
   * Pinned values with no results are omitted. Hidden values are removed first.
   * @param {string} facetName
   * @param {object} facetValues - { [value]: count }
   * @param {object} valueOrdering - renderingContent.facetOrdering.values[facetName]
   * @returns {string[]}
   */
  orderFacetValues(facetName, facetValues, valueOrdering) {
    if (!facetValues) {
      return [];
    }

    const hiddenValues = new Set((valueOrdering && valueOrdering.hide) || []);
    const pinnedValues =
      (valueOrdering && Array.isArray(valueOrdering.order) && valueOrdering.order) ||
      [];
    const sortRemainingBy = (
      (valueOrdering && valueOrdering.sortRemainingBy) ||
      ''
    ).toLowerCase();

    const remainingCounts = {};
    Object.keys(facetValues).forEach((value) => {
      if (!hiddenValues.has(value)) {
        remainingCounts[value] = facetValues[value];
      }
    });

    const orderedValues = [];
    pinnedValues.forEach((value) => {
      if (Object.prototype.hasOwnProperty.call(remainingCounts, value)) {
        orderedValues.push(value);
        delete remainingCounts[value];
      }
    });

    if (sortRemainingBy === 'hidden') {
      return orderedValues;
    }

    const remainingValues = Object.keys(remainingCounts);

    if (sortRemainingBy === 'alpha') {
      remainingValues.sort((a, b) => a.localeCompare(b));
    } else if (sortRemainingBy === 'count' || valueOrdering) {
      remainingValues.sort(
        (a, b) => remainingCounts[b] - remainingCounts[a] || a.localeCompare(b)
      );
    } else if (facetName === 'price_range') {
      remainingValues.sort((a, b) => {
        return parseFloat(a.split(':')[0]) - parseFloat(b.split(':')[0]);
      });
    } else {
      remainingValues.sort(
        (a, b) => remainingCounts[b] - remainingCounts[a] || a.localeCompare(b)
      );
    }

    return orderedValues.concat(remainingValues);
  }

  getFacetValuesFromResults(results, facetLocation, facetName) {
    const resultIndex =
      facetLocation && facetLocation[facetName] != null
        ? facetLocation[facetName]
        : 0;
    const result = results[resultIndex] || results[0];

    return result && result.facets ? result.facets[facetName] : undefined;
  }

  /**
   * Generate the filters from the search results, honoring Algolia facet display.
   * Selected refinements stay in pills even if a facet is omitted from the list.
   * @param {object[]} results - The results from the Algolia API.
   * @param {object} facetLocation - Disjunctive query index per applied facet.
   */
  generateFiltersFromSearchResults(results, facetLocation) {
    const openFilterTypes = this.getOpenFilterTypes();

    if (this.filtersDesktop) {
      this.filtersDesktop.innerHTML = '';
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.innerHTML = '';
    }

    const facetOrdering = this.getFacetOrdering(results[0]);
    const orderedFacetNames = this.getOrderedFacetNames(facetOrdering);
    const valueOrdering =
      (facetOrdering && facetOrdering.values) || {};

    orderedFacetNames.forEach((facetName, facetIndex) => {
      const facetValues = this.getFacetValuesFromResults(
        results,
        facetLocation,
        facetName
      );

      if (!facetValues) {
        return;
      }

      const orderedValues = this.orderFacetValues(
        facetName,
        facetValues,
        valueOrdering[facetName]
      );

      if (!orderedValues.length) {
        return;
      }

      this.generateFilters(
        this.getFacetConfig(facetName),
        orderedValues,
        facetIndex,
        openFilterTypes
      );
    });
  }

  /**
   * Generate the filters from the facet values.
   * @param {object} facet - The facet from the algoliaShopify API.
   * @param {string[]} orderedValues - Facet values in display order.
   * @param {number} facetIndex - The index of the facet.
   * @param {array} openFilterTypes - The open filter types.
   */
  generateFilters(facet, orderedValues, facetIndex, openFilterTypes) {
    const details = document.createElement('details');
    details.id = `Filter-${this.sectionId}-${facetIndex}`;
    details.classList.add('filters__details', 'border');
    details.dataset.index = `mobile-${this.currentPage}`;

    if (openFilterTypes.includes(details.id)) {
      details.open = true;
    }

    const summary = document.createElement('summary');
    summary.classList.add('filters__summary', 'h3');
    // textContent: fallback titles are derived from Algolia attribute names.
    const summaryLabel = document.createElement('span');
    summaryLabel.textContent = facet.title;
    summary.appendChild(summaryLabel);
    details.appendChild(summary);

    const div = document.createElement('div');
    div.classList.add('mobile-facets__submenu');
    details.appendChild(div);

    const ul = document.createElement('ul');
    ul.classList.add('filters__list', 'list--unstyled');
    div.appendChild(ul);

    orderedValues.forEach((value, valueIndex) => {
      const li = document.createElement('li');
      li.classList.add('filters__item', 'list-menu__item');
      ul.appendChild(li);

      const label = document.createElement('label');
      label.classList.add('facets__label', 'filters__label');
      label.htmlFor = `Filter-${this.sectionId}-${facetIndex}-${valueIndex}`;
      li.appendChild(label);

      const input = document.createElement('input');
      input.classList.add('mobile-facets__checkbox');
      input.id = `Filter-${this.sectionId}-${facetIndex}-${valueIndex}`;
      input.type = 'checkbox';
      input.name = facet.name;
      input.value = value;
      input.checked =
        this.currentFilters[facet.name] &&
        this.currentFilters[facet.name].includes(value);

      const span = document.createElement('span');
      span.classList.add('facet-checkbox__text', 'facet-checkbox__text-label');

      const labelId = algoliaStringify(value);

      if (facet.name == 'named_tags.Colour') {
        ul.classList.add('filters__list--grid');

        input.classList.add('visually-hidden');
        span.classList.add('visually-hidden');

        label.classList.add('filters__label--swatch');
        label.dataset.value = value.toLowerCase();
        label.setAttribute('title', value);       

        span.innerHTML = `${value}`;
      } else if (facet.name == 'options.size') {
        ul.classList.add('filters__list--grid');

        input.classList.add('visually-hidden');

        label.classList.add('filters__label--size');
        span.innerHTML = `${value}`; // TRANSLATIONS
      } else if (facet.name == 'price_range') {
        if (value == '0:10') {
          span.innerHTML = window.language.under_10;
        } else if (value.indexOf(':') > -1) {
          const priceRange = value.split(':');
          span.innerHTML = `$${priceRange[0]} - $${priceRange[1]}`;
        } else {
          span.innerHTML = `${value}`;
        }
      } else if (facet.name == 'named_tags.Discount Amount') {
        span.innerHTML = `${(parseFloat(value) * 100).toFixed(0)}%`;
      } else {
        span.innerHTML = `${value}`; // TRANSLATIONS
      }

      label.appendChild(span);
      label.appendChild(input);
    });

    if (this.filtersDesktop) {
      const clone = details.cloneNode(true);

      clone.id = `${clone.id}-desktop`;

      clone.querySelectorAll('[id]').forEach((element) => {
        element.id = `${element.id}-desktop`;
      });

      clone.querySelectorAll('[for]').forEach((element) => {
        element.setAttribute('for', `${element.getAttribute('for')}-desktop`);
      });

      this.filtersDesktop.appendChild(clone);
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.appendChild(details.cloneNode(true));
    }
  }

  /**
   * Generate the product cards from the hits.
   * @param {object} hits - The product hits from the algoliaShopify API.
   */
  generateProductsFromHits(hits, queryId) {
    const powerReviewProducts = [];

    hits.forEach((hit, index) => {
      const li = document.createElement('li');
      li.classList.add('grid__item');
      //li.dataset.cascade = true;
      //li.style.setProperty('--animation-order', `${index + this.indexAdjust}`);

      const product = document.createElement('card-component');
      product.classList.add('card', `color-${window.cards.color_scheme}`);

      product.dataset.productId = hit.id;
      product.dataset.objectId = hit.objectID;
      product.dataset.position = index + this.indexAdjust;
      product.dataset.queryId = queryId;

      // Map Algolia hit fields to Shopify Liquid equivalents
      product.dataset.id = hit.id; // data-id
      product.dataset.handle = hit.handle || ''; // data-handle
      product.dataset.url = `${
        this.collectionHandle
          ? `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/collections/${
              this.collectionHandle
            }`
          : ''
      }/products/${hit.handle}`; // data-url
      product.dataset.title = hit.title || ''; // data-title
      product.dataset.vendor = hit.vendor || hit.brand || ''; // data-vendor
      product.dataset.type = hit.product_type || hit.type || ''; // data-type
      product.dataset.price =
        typeof hit.price === 'number' ? hit.price : parseFloat(hit.price) || 0; // data-price (no currency)

      const imageLink = document.createElement('a');
      imageLink.classList.add('card__link', 'link--unstyled');
      imageLink.href = `${
        this.collectionHandle
          ? `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/collections/${
              this.collectionHandle
            }`
          : ''
      }/products/${hit.handle}`;
      imageLink.addEventListener('click', this.onLinkClick.bind(this));

      const imageWrapper = document.createElement('div');
      imageWrapper.classList.add('card__image-wrapper');

      if (hit.meta?.custom?.global_access) {
        product.classList.add('card--global-access');
      }

      if (
        hit.meta?.custom?.coming_soon &&
        hit.meta?.custom?.coming_soon == true
      ) {
        const badge = document.createElement('div');
        badge.classList.add('badge', 'card__price-badge', 'badge--coming-soon');
        badge.innerHTML = `
          ${window.cards.coming_soon}
        `;
        imageWrapper.appendChild(badge);
      } else if (hit.compare_at_price > hit.price) {
        const badge = document.createElement('div');
        badge.classList.add('badge', 'card__price-badge', 'badge--percent-off');
        const percentOff =
          ((hit.compare_at_price - hit.price) / hit.compare_at_price) * 100;
        badge.innerHTML = `
          ${JDRounding(percentOff)}${window.cards.percent_off}
        `;
        imageWrapper.appendChild(badge);
      }

      // Badges stacked in the bottom-left corner, numbered by their position
      // in the stack. Staff Pick goes first so it sits in the same spot on
      // every card, with any custom badges stacked above it.
      let stackedBadgeCount = 0;

      if (shouldShowStaffPick(hit)) {
        stackedBadgeCount++;

        const staffPickBadge = document.createElement('div');
        staffPickBadge.classList.add(
          'badge',
          'card__price-badge',
          `badge--${stackedBadgeCount}`,
          'badge--staff-pick'
        );

        // Takes its background and text colors from the chosen scheme. Scheme
        // IDs are class-safe, but a value with spaces would make classList
        // throw and stop the grid rendering, so it is checked first.
        const colorScheme = String(window.cards.staff_pick_color_scheme || '');

        if (/^[\w-]+$/.test(colorScheme)) {
          staffPickBadge.classList.add(`color-${colorScheme}`);
        }

        // textContent, so a label with markup in it renders as plain text.
        staffPickBadge.textContent = String(
          window.cards.staff_pick_label
        ).trim();
        imageWrapper.appendChild(staffPickBadge);
      }

      if (hit.meta?.custom?.custom_badges) {
        // A malformed metafield must only drop this product's custom badges,
        // not stop the rest of the grid from rendering.
        let customBadges = [];

        try {
          const parsedBadges = JSON.parse(hit.meta.custom.custom_badges);
          customBadges = Array.isArray(parsedBadges) ? parsedBadges : [];
        } catch (error) {
          console.warn(
            'Invalid custom_badges metafield, skipping custom badges for product',
            hit.id,
            error
          );
        }

        // Add the handleized custom badge to the class
        customBadges.forEach((customBadge) => {
          stackedBadgeCount++;

          const customBadgeElement = document.createElement('div');
          customBadgeElement.classList.add(
            'badge',
            'card__price-badge',
            `badge--${stackedBadgeCount}`,
            `badge--${handleize(customBadge)}`
          );
          customBadgeElement.innerHTML = customBadge;
          imageWrapper.appendChild(customBadgeElement);
        });
      }

      if (window.theme.wishlist_enabled && hit) {
        const wishlistContainer = document.createElement('div');
        wishlistContainer.classList.add('wishlist__button');

        const wishlistButton = document.createElement('button');
        wishlistButton.classList.add(
          'link',
          'swym__button',
          'product_' + hit.id
        );
        wishlistButton.dataset.withEpi = 'true';
        wishlistButton.dataset.swaction = 'addToWishlist';
        wishlistButton.dataset.productId = hit.id;
        wishlistButton.dataset.variantId = hit.objectID;
        wishlistButton.dataset.productUrl = `${location.origin}/products/${hit.handle}`;

        wishlistContainer.appendChild(wishlistButton);
        imageWrapper.appendChild(wishlistContainer);
      }

      const imageContainer = document.createElement('div');
      imageContainer.classList.add('card__image-container');

      let imageList = [];
      if (hit.meta?.custom?.images) {
        imageList = hit.meta.custom.images.split(',');
      } else {
        imageList = [hit.image];
      }

      const slider = document.createElement('ul');
      slider.classList.add(
        'slider',
        'grid',
        'grid--1-col',
        'slider--desktop',
        'slider--mobile'
      );
      slider.setAttribute('aria-label', 'Product Images');

      imageList.forEach((src) => {
        const li = document.createElement('li');
        li.classList.add('grid__item', 'slider__slide');

        const mediaContainer = document.createElement('div');
        mediaContainer.classList.add(
          'media',
          `media--${window.cards.aspect_ratio}`
        );

        const image = document.createElement('img');
        image.classList.add('motion-reduce');
        image.loading = 'lazy';
        image.width = 540;
        image.height = 540;
        image.src = src + '&width=540';
        image.srcset = `${src + '&width=180'} 180w, ${
          src + '&width=360'
        } 360w, ${src + '&width=540'} 540w, ${src + '&width=720'} 720w, ${
          src + '&width=900'
        } 900w, ${src + '&width=1080'} 1080w`;
        image.alt = hit.title;
        image.sizes =
          '(min-width: 750px) calc((100vw - 1rem) / 4), calc((100vw - 1rem) / 2)';

        mediaContainer.appendChild(image);
        li.appendChild(mediaContainer);
        slider.appendChild(li);
      });

      imageContainer.appendChild(slider);
      imageWrapper.appendChild(imageContainer);
      imageLink.appendChild(imageWrapper);

      const details = document.createElement('div');
      details.classList.add('card__details');

      const title = document.createElement('div');
      title.classList.add('card__title');

      const span = document.createElement('a');
      span.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${
        hit.handle
      }`;
      span.classList.add('card__title-title', 'h4', 'link--unstyled');
      span.innerHTML = hit.title;

      title.appendChild(span);
      details.appendChild(title);

      if (hit.meta?.custom?.subtitle) {
        const subtitle = document.createElement('div');
        subtitle.classList.add('card__subtitle', 'body');
        subtitle.innerHTML = hit.meta.custom.subtitle;
        details.appendChild(subtitle);
      }

      const priceContainer = document.createElement('div');
      priceContainer.classList.add('card__price');

      const price = document.createElement('div');
      price.classList.add('price');
      if (hit.compare_at_price > hit.price) {
        price.classList.add('price--on-sale');
      }

      price.innerHTML = `
        <div class="price__sale">
          <span class="visually-hidden visually-hidden--inline">Sale Price</span>
          <span class="price-item">
            $${hit.price}
          </span>
        </div>            

        ${
          hit.compare_at_price > hit.price
            ? `
          <div class="price__regular body body--small gray">
            <span class="visually-hidden visually-hidden--inline">Regular Price</span>
            <s class="price-item">
              $${hit.compare_at_price}
            </s>
          </div>
        `
            : ''
        }
      `;

      priceContainer.appendChild(price);
      details.appendChild(priceContainer);

      if (window.theme.reviews.enabled) {
        const reviews = document.createElement('div');
        reviews.classList.add('card__reviews');
        reviews.id = `CardReview-${hit.id}`;

        details.appendChild(reviews);
      }

      if (hit.named_tags['#.Group']) {
        const cardGroup = document.createElement('card-group');
        cardGroup.classList.add('card__swatches');
        cardGroup.dataset.groupId = hit.named_tags['#.Group'];
        cardGroup.dataset.productHandle = hit.handle;
        cardGroup.dataset.productUrl = `/products/${hit.handle}`;
        cardGroup.dataset.limitSwatches = 'true';
        cardGroup.dataset.reorderSwatches = 'true';
        cardGroup.dataset.waitUntilLoaded = 'true';
        cardGroup.dataset.hardLoad = 'true';
        details.appendChild(cardGroup);

        const skeleton = document.createElement('div');
        skeleton.classList.add('skeleton');
        cardGroup.appendChild(skeleton.cloneNode(true));
        cardGroup.appendChild(skeleton.cloneNode(true));
      } else {
        const swatchesPlaceholder = document.createElement('div');
        swatchesPlaceholder.classList.add(
          'card__swatches',
          'card__swatches--placeholder'
        );
        swatchesPlaceholder.setAttribute('aria-hidden', 'true');
        details.appendChild(swatchesPlaceholder);
      }

      product.appendChild(imageLink);
      product.appendChild(details);

      if (
        window.cards.enable_quickview ||
        window.cards.enable_quickview_mobile
      ) {
        const quickviewTrigger = document.createElement('quickview-trigger');
        quickviewTrigger.classList.add('card__quickview');

        if (!window.cards.enable_quickview) {
          quickviewTrigger.classList.add('hidden--desktop');
        }

        if (!window.cards.enable_quickview_mobile) {
          quickviewTrigger.classList.add('hidden--mobile');
        }
        const root = window.Shopify?.routes?.root || '';
        
        quickviewTrigger.dataset.productHandle = hit.handle;
        quickviewTrigger.dataset.productUrl = `${window.location.origin}/${root}/products/${hit.handle}`;

        const quickviewButton = document.createElement('button');
        quickviewButton.classList.add(
          'button',
          'button--secondary',
          'button--small'
        );
        quickviewButton.innerHTML = `
          ${window.cards.quickbuy}
        `;
        quickviewTrigger.appendChild(quickviewButton);

        product.appendChild(quickviewTrigger);
      }

      li.appendChild(product);
      this.productList.appendChild(li);

      const pageTitle = (document.title || "").split(/\s[-–—]\s/)[0].trim();
      if (this.collectionPromos) {
        const promos = Shopify.locale == "fr" ? this.collectionPromos.tiles_fr : this.collectionPromos.tiles;
        const bannerData = Shopify.locale == "fr" ? this.collectionPromos.banner_fr : this.collectionPromos.banner;

        if(promos.length > 0) {
          promos.forEach((tile, tileIndex) => {
            if (
              index + this.indexAdjust == tile.position &&
              (tile.image || tile.video || tile.video_mobile)
            ) {
              const tileLi = document.createElement('li');
              tileLi.classList.add('grid__item', 'promo-tile');

              const tileMedia = document.createElement('div');
              tileMedia.classList.add('media', 'media--collection');
              if (tile.link == '' && tile.name != '') {
                tileMedia.dataset.promoTileName = tile.name;
              }

              const desktopW = Number(tile.width) || 0;
              const desktopH = Number(tile.height) || 0;
              const mobileW = Number(tile.width_mobile) || desktopW;
              const mobileH = Number(tile.height_mobile) || desktopH;
              if (desktopW > 0 && desktopH > 0) {
                tileLi.style.setProperty(
                  '--tile-aspect-desktop',
                  `${desktopW} / ${desktopH}`
                );
              }
              if (mobileW > 0 && mobileH > 0) {
                tileLi.style.setProperty(
                  '--tile-aspect-mobile',
                  `${mobileW} / ${mobileH}`
                );
              }

              /**
               * Keep imgs/videos as direct children of .media so theme rules
               * (.media > * and .media img/video) match the original layout.
               * Use hidden--mobile / hidden--desktop for art direction (see base.css).
               */
              const canPlayVideo = !prefersReducedMotion;
              const desktopVideoSrc =
                canPlayVideo && tile.video ? tile.video : null;
              const mobileVideoSrc =
                canPlayVideo && tile.video_mobile ? tile.video_mobile : null;
              const useArtDirection = Boolean(
                tile.video_mobile || tile.image_mobile
              );

              if (useArtDirection) {
                if (desktopVideoSrc) {
                  tileMedia.appendChild(
                    createPromoTileVideo({
                      src: desktopVideoSrc,
                      poster: tile.image,
                      width: tile.width,
                      height: tile.height,
                      className: 'hidden--mobile',
                    })
                  );
                } else if (tile.image) {
                  tileMedia.appendChild(
                    createPromoTileImage({
                      src: tile.image,
                      width: tile.width,
                      height: tile.height,
                      className: 'hidden--mobile',
                    })
                  );
                }

                if (mobileVideoSrc) {
                  tileMedia.appendChild(
                    createPromoTileVideo({
                      src: mobileVideoSrc,
                      poster: tile.image_mobile || tile.image,
                      width: tile.width_mobile || tile.width,
                      height: tile.height_mobile || tile.height,
                      className: 'hidden--desktop',
                    })
                  );
                } else if (tile.image_mobile || tile.image) {
                  tileMedia.appendChild(
                    createPromoTileImage({
                      src: tile.image_mobile || tile.image,
                      width: tile.width_mobile || tile.width,
                      height: tile.height_mobile || tile.height,
                      className: 'hidden--desktop',
                    })
                  );
                }
              } else if (desktopVideoSrc || mobileVideoSrc) {
                tileMedia.appendChild(
                  createPromoTileVideo({
                    src: desktopVideoSrc || mobileVideoSrc,
                    poster: tile.image,
                    width: tile.width,
                    height: tile.height,
                  })
                );
              } else if (tile.image) {
                tileMedia.appendChild(
                  createPromoTileImage({
                    src: tile.image,
                    width: tile.width,
                    height: tile.height,
                  })
                );
              }

              const ctaLabel =
                tile.cta_label && String(tile.cta_label).trim();

              // Media is clickable when a link is set; CTA button below when label + link are set
              if (tile.link != '') {
                const mediaLink = document.createElement('a');
                mediaLink.href = tile.link;
                mediaLink.classList.add(
                  'link',
                  'link--unstyled',
                  'media',
                  'media--collection',
                  'promo-tile__media-link'
                );
                const promoFallbackLabel =
                  Shopify.locale == 'fr' ? 'Voir la promotion' : 'View promotion';
                mediaLink.setAttribute(
                  'aria-label',
                  String(tile.title || ctaLabel || tile.name || '').trim() ||
                    promoFallbackLabel
                );
                if (tile.name != '') {
                  mediaLink.dataset.promoTileName = tile.name;
                  mediaLink.dataset.internalPromotionName =
                    'Promo Card - ' + tile.name;
                  mediaLink.dataset.internalPromotionId =
                    'Promo Card - ' + tile.name;
                  mediaLink.dataset.internalCreativeName =
                    tile.video || tile.image;
                  mediaLink.dataset.internalCreativeSlot =
                    pageTitle + ' - Promo Card';
                }
                mediaLink.appendChild(tileMedia);
                tileLi.appendChild(mediaLink);
              } else {
                tileLi.appendChild(tileMedia);
              }

              if (tile.link != '' && ctaLabel) {
                const cta = document.createElement('a');
                cta.href = tile.link;
                const isPrimary =
                  String(tile.cta_type || '')
                    .trim()
                    .toLowerCase() === 'primary button';
                cta.classList.add('button', 'button--small', 'promo-tile__cta');
                if (!isPrimary) {
                  cta.classList.add('button--secondary');
                }
                cta.textContent = ctaLabel;
                if (tile.name != '') {
                  cta.dataset.promoTileName = tile.name;
                  cta.dataset.internalPromotionName =
                    'Promo Card - ' + tile.name;
                  cta.dataset.internalPromotionId =
                    'Promo Card - ' + tile.name;
                  cta.dataset.internalCreativeName =
                    tile.video || tile.image;
                  cta.dataset.internalCreativeSlot =
                    pageTitle + ' - Promo Card';
                }
                tileLi.appendChild(cta);
              }

              this.productList.appendChild(tileLi);
              this.indexAdjust++;
            }
          });
          observePromoLoopVideos(this);
        }

        if (
          index + this.indexAdjust == 7 &&
          bannerData.image &&
          Number(bannerData.width) > 0 &&
          Number(bannerData.height) > 0
        ) {
          const banner = document.createElement('div');
          banner.classList.add('listing__banner');
          banner.innerHTML = `
          <a href="${bannerData.link}"
   class="link link--unstyled listing__banner-link" ${
            bannerData.promo_banner_name
              ? `
   data-internal-creative-name="${bannerData.image}"
   data-internal-creative-slot="${pageTitle} - Mid Banner"
   data-internal-promotion-id="Mid Banner - ${bannerData.promo_banner_name}"
   data-internal-promotion-name="Mid Banner - ${bannerData.promo_banner_name}"` : ""
          }>

            <div class='media media--adapt' style='padding-bottom: ${
              (bannerData.height /
                bannerData.width) *
              100
            }%;'>
              <img src="${bannerData.image}" alt="${
            bannerData.title || ''
          }" class="listing__banner-image">
            </div>
          </a>
        `;

          this.productList.appendChild(banner);
        }
      }

      const cardGroup = li.querySelector('card-group');
      if (cardGroup) {
        cardGroup.setup();
      }

      powerReviewProducts.push({
        api_key: window.theme.reviews.api_key,
        locale: window.theme.reviews.locale,
        merchant_group_id: window.theme.reviews.group_id,
        merchant_id: window.theme.reviews.merchant_id,
        page_id: hit.id,
        components: {
          CategorySnippet: `CardReview-${hit.id}`,
        },
      });
    });

    this.indexAdjust += hits.length;

    if (window.theme.wishlist_enabled) {
      document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
    }

    if (window.theme.reviews.enabled) {
      setTimeout(() => {
        pwr('render', powerReviewProducts);
      }, 150);
    }
  }

  generateCountsFromResults(results) {
    this.itemCounts.forEach((count) => {
      count.innerHTML = `(${results.nbHits} ${window.paginationStrings.items})`;
    });
  }

  /**
   * Generate the pagination from the results.
   * @param {object} results - The results from the algoliaShopify API.
   */
  generatePaginationFromResults(results) {
    if (results.nbPages == 0) {
      this.paginationContainer.remove();
      return;
    }

    /*
    this.paginationContainer.dataset.hasMore =
      results.page + 1 != results.nbPages;

    this.paginationContainer.style.setProperty(
      '--fill',
      `${
        ((results.page * results.hitsPerPage + results.hits.length) /
          results.nbHits) *
        100
      }%`
    );
    */

    const ofText = this.paginationContainer.querySelector('.pagination__of');
    const select = this.paginationContainer.querySelector(
      '.pagination__select'
    );
    const prevButton = this.paginationContainer.querySelector(
      '.pagination__button--prev'
    );
    const nextButton = this.paginationContainer.querySelector(
      '.pagination__button--next'
    );

    if (results.page == 0) {
      prevButton.disabled = true;
    } else {
      prevButton.disabled = false;
    }

    if (results.page == results.nbPages - 1) {
      nextButton.disabled = true;
    } else {
      nextButton.disabled = false;
    }

    ofText.innerHTML = window.paginationStrings.of.replace(
      '{{ number }}',
      results.nbPages
    );

    // Populate the select with options for each page
    select.innerHTML = '';
    for (let i = 0; i < results.nbPages; i++) {
      const option = document.createElement('option');
      option.value = i;
      option.innerHTML = i + 1;
      select.appendChild(option);

      if (i == results.page) {
        option.selected = true;
      }
    }

    /*
    this.paginationContainer.querySelector('.pagination__text').innerHTML =
      window.paginationStrings.viewed
        .replace(
          '{{ number }}',
          results.page * results.hitsPerPage + results.hits.length
        )
        .replace('{{ total }}', results.nbHits);

    if (results.page != results.nbPages) {
      this.paginationContainer.querySelector(
        '.pagination__more button'
      ).innerHTML = window.paginationStrings.show_more.replace(
        '{{ number }}',
        results.hitsPerPage
      );
    }
    */
  }

  generatePillsFromFacets() {
    this.pillsContainer.innerHTML = '';
this.cleanEmptyFilters();

if (!Object.keys(this.currentFilters).length) {
  this.pillsContainer.innerHTML = "";
  return;
}
    Object.keys(this.currentFilters).forEach((filterGroup) => {
      this.currentFilters[filterGroup].forEach((filter) => {
        const pill = document.createElement('button');
        pill.classList.add('filters__pill');
        pill.dataset.filterGroup = filterGroup;
        pill.dataset.filter = filter;

        // price - use textContent to prevent XSS from URL parameters
        if (filterGroup == 'price_range') {
          const parts = filter.split(':');
          pill.textContent = `$${parts[0]} - $${parts[1]}`;
        } else {
          pill.textContent = filter;
        }

        const span = document.createElement('span');
        span.classList.add('visually-hidden');
        span.textContent = `Remove filter: ${filter}`;

        pill.appendChild(span);

        pill.addEventListener('click', this.onFilterPillClick.bind(this));

        this.pillsContainer.appendChild(pill);
      });
    });

    if (Object.keys(this.currentFilters).length != 0) {
      const pill = document.createElement('a');
      pill.classList.add('link', 'link--underlined', 'filters__pill--clear');
      pill.href = `${window.location.pathname}`;
      pill.textContent = window.language.clear_all;

      const span = document.createElement('span');
      span.classList.add('visually-hidden');
      span.textContent = 'Remove all filters';

      pill.appendChild(span);

      this.pillsContainer.appendChild(pill);
    }
  }

  /**
   * Get the open filter types. This is used to maintain the open/closed state of the filters.
   * @returns {array} The open filter types.
   */
  getOpenFilterTypes() {
    const openFiltersDesktop = [];
    const openFiltersDrawer = [];

    if (this.filtersDesktop) {
      this.filtersDesktop.querySelectorAll('details').forEach((details) => {
        if (details.open) {
          openFiltersDesktop.push(details.id);
        }
      });
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.querySelectorAll('details').forEach((details) => {
        if (details.open) {
          openFiltersDrawer.push(details.id);
        }
      });
    }

    return openFiltersDesktop.concat(openFiltersDrawer);
  }
  cleanEmptyFilters() {
    Object.keys(this.currentFilters).forEach((key) => {
      if (
        !this.currentFilters[key] ||
        this.currentFilters[key].length === 0
      ) {
        delete this.currentFilters[key];
      }
    });
  }

  onFilterPillClick(event) {
    const target = event.target.closest("[data-filter-group]");
    if (!target) return;

    const filterGroup = target.dataset.filterGroup;
    const filter = target.dataset.filter;

    const input = this.querySelector(
      `input[name="${filterGroup}"][value="${CSS.escape(filter)}"]`,
    );

    if (input) {
      input.checked = false;
      this.onFilterChange(input);
      return;
    }
    if (this.currentFilters[filterGroup]) {
      this.currentFilters[filterGroup] = this.currentFilters[
        filterGroup
      ].filter((v) => v !== filter);

      if (this.currentFilters[filterGroup].length === 0) {
        delete this.currentFilters[filterGroup];
      }
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;

    const values = params.getAll(filterGroup);

    if (values.length) {
      const newValues = values.filter((v) => v !== filter);

      params.delete(filterGroup);
      newValues.forEach((v) => params.append(filterGroup, v));
    }

    window.history.replaceState({}, "", url.pathname + "?" + params.toString());

    this.search();
  }

  /**
   * Handle the filter change.
   * @param {object} event - The event object.
   */
  onFilterChange(event) {
    var target = event;

    if (event.target) {
      target = event.target;
    }

    const form = target.closest('form');

    const name = target.name;
    const value = target.value;

    if (!this.currentFilters[name]) {
      this.currentFilters[name] = [];
    }

    if (target.checked) {
      this.currentFilters[name].push(value);

      if (window.theme.dynamic_yield_enabled && DynamicYield) {
        DynamicYield.filterItems(target.name, target.value);

        if (aa) {
          aa('clickedFilters', {
            userToken: getAlgoliaUserToken(),
            index: this.buildIndexParams(),
            eventName: 'PLP: Filter Clicked',
            filters: [`${target.name}:${target.value}`],
          });
        }
      }
    } else {
      this.currentFilters[name] = this.currentFilters[name].filter(
        (item) => item !== value
      );
    }

    // Facet values can contain quotes (e.g. a brand name), so escape them.
    const siblingSelector = `input[name="${CSS.escape(target.name)}"][value="${CSS.escape(target.value)}"]`;

    if (form == this.filtersDesktop) {
      const siblingInput = this.filtersDrawer.querySelector(siblingSelector);

      if (siblingInput) {
        siblingInput.checked = target.checked;
      }
    }

    if (this.filtersDesktop && form == this.filtersDrawer) {
      const siblingInput = this.filtersDesktop.querySelector(siblingSelector);

      if (siblingInput) {
        siblingInput.checked = target.checked;
      }
    }

    this.updateURLHash();
    this.search();
  }

  /**
   * Handle the sort change.
   * @param {object} event - The event object.
   */
  onSortChange(event) {
    this.currentSortBy = event.target.value;

    if (event.target.id == 'AlgoliaDrawerSortBy') {
      this.sortBy.value = event.target.value;
    } else {
      this.sortByDrawer.querySelectorAll('input').forEach((input) => {
        if (input.value == event.target.value) {
          input.checked = true;
        } else {
          input.checked = false;
        }
      });
    }

    if (window.theme.dynamic_yield_enabled && DynamicYield) {
      const order =
        event.target.value.split('_').pop() === 'asc' ? 'asc' : 'desc';
      DynamicYield.sortItems(event.target.value, order);
    }

    this.updateURLHash();
    this.search();
  }

  /**
   * Handle the pagination events.
   * @param {object} event - The event object.
   */
  onPaginationClick(event) {
    this.currentPage++;
    this.search(false);
  }

  onPaginationSelect(event) {
    this.currentPage = event.target.value;

    this.updateURLHash();

    this.search(false, true);
  }

  onPaginationPrev(event) {
    this.currentPage--;

    this.updateURLHash();

    this.search(false, true);
  }

  onPaginationNext(event) {
    this.currentPage++;

    this.updateURLHash();

    this.search(false, true);
  }

  onLinkClick(event) {
    const card = event.target.closest('.card');

    const position = parseInt(card.dataset.position) + 1;
    
    aa('clickedObjectIDsAfterSearch', {
      userToken: getAlgoliaUserToken(),
      eventName: 'PLP: Search Result Clicked',
      index: this.buildIndexParams(),
      queryID: card.dataset.queryId,
      objectIDs: [card.dataset.objectId],
      positions: [position],
    });

    addToAlgoliaStorage(card.dataset.productId, card.dataset.queryId, this.buildIndexParams());
  }

  /**
   * Update the URL hash.
   */
  updateURLHash() {
  const params = new URLSearchParams();

  if (this.searchTerms) {
    params.set('q', this.searchTerms);
  }
this.cleanEmptyFilters();

  Object.keys(this.currentFilters).forEach((filter) => {
    this.currentFilters[filter].forEach((value) => {
      params.append(filter, value);
    });
  });

  if (this.currentSortBy) {
    params.set('sort_by', this.currentSortBy);
  }

  if (this.currentPage !== 0) {
    params.set('page', this.currentPage);
  }

  const queryString = params.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  history.pushState(
    {
      page: this.currentPage,
      searchParams: this.currentFilters,
      sortBy: this.currentSortBy,
    },
    '',
    newUrl
  );
}

}

customElements.define('listing-algolia', ListingAlgolia);

class ListingComponent extends HTMLElement {
  constructor() {
    super();

    this.filterIds = ['FiltersForm', 'FiltersFormDrawer'];

    this.template = this.dataset.template;
    this.sectionId = this.dataset.sectionId;
    this.cache = [];

    this.initialSearchParams = window.location.search.slice(1);
    this.currentSearchParams = window.location.search.slice(1);

    this.hasFilters = false;

    this.forms = this.querySelectorAll('listing-form form');

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.forms.forEach((form) => {
      form.addEventListener('input', this.debouncedOnSubmit.bind(this));

      if (this.filterIds.indexOf(form.id) > -1) {
        this.hasFilters = true;
      }
    });

    window.addEventListener('popstate', (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : this.initialSearchParams;

      if (searchParams === this.currentSearchParams) return;

      this.renderPage(searchParams, null, false);
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const forms = [];

    this.forms.forEach((form) => {
      if (this.filterIds.indexOf(form.id) > -1) {
        if (form.contains(event.target)) {
          forms.push(this.createSearchParams(form));
        }
      } else {
        forms.push(this.createSearchParams(form));
      }
    });

    this.renderPage(forms.join('&'), event);
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    const priceRangeEntries = Array.from(formData.entries()).filter(([key]) =>
      key.startsWith('price_range')
    );

    if (priceRangeEntries.length > 0) {
      var priceMin = 99999999;
      var priceMax = 0;

      priceRangeEntries.forEach(([key, value]) => {
        const [min, max] = value.split('-');

        if (min < priceMin) {
          priceMin = min;
        }

        if (max > priceMax) {
          priceMax = max;
        }
      });

      formData.append(`filter.v.price.gte`, (priceMin / 100).toFixed(2));

      formData.append(`filter.v.price.lte`, (priceMax / 100).toFixed(2));

      formData.delete('price_range');
    }

    return new URLSearchParams(formData).toString();
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.currentSearchParams = searchParams;

    this.classList.add('loading');

    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;

    this.cache.some((element) => element.url === url)
      ? this.renderSectionFromCache(url, event)
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        this.buildPage(url, html, event);
      });
  }

  renderSectionFromCache(url, event) {
    const html = this.cache.find((element) => element.url === url).html;

    this.buildPage(url, html, event);
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  buildPage(url, html, event) {
    this.cache.push({ html, url });

    if (this.hasFilters) {
      this.buildFilters(html, event);
    }

    this.buildProducts(html, event);

    this.classList.remove('loading');
  }

  buildFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    this.filterIds.forEach((filterId) => {
      const filterFromFetch = parsedHTML.getElementById(filterId);
      const filterFromDom = document.getElementById(filterId);

      if (!filterFromFetch) return;
      if (!filterFromDom) return;

      const detailsFromFetch = parsedHTML.querySelectorAll(
        `#${filterId} details`
      );
      const detailsFromDom = document.querySelectorAll(`#${filterId} details`);

      var openDetails = [];
      var closedDetails = [];

      detailsFromDom.forEach((detail) => {
        if (detail.open) {
          openDetails.push(detail.id);
        } else {
          closedDetails.push(detail.id);
        }
      });

      detailsFromFetch.forEach((detail) => {
        if (openDetails.indexOf(detail.id) > -1) {
          detail.open = true;
        } else {
          detail.open = false;
        }
      });

      filterFromDom.innerHTML = filterFromFetch.innerHTML;
    });

    if (event) {
      const selectedInput = document.querySelector(`#${event.target.id}`);

      if (selectedInput) {
        selectedInput.focus();
      }
    }
  }

  buildProducts(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    document.getElementById('ProductGridContainer').innerHTML =
      parsedHTML.getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.remove('scroll-trigger');
        element.classList.remove('animate--slide-in');
      });
  }
}

customElements.define('listing-component', ListingComponent);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class LoadMore extends HTMLElement {
  constructor() {
    super();

    this.params = this.dataset.params;

    this.listing = this.closest('listing-component');

    this.button = this.querySelector('.pagination__more');

    this.button.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick() {
    this.listing.renderPage(this.params, null, false);
  }
}

customElements.define('load-more', LoadMore);
